import { Link } from 'react-router-dom';
import { Linkedin, Facebook, Instagram, Phone, Mail, MapPin, CalendarDays } from 'lucide-react';
import { getContact, getSocials, getFooterCompany, getFooterServices } from '@/data/site';
import { useLiveTick } from '@/lib/liveContent';

const SOCIAL_ICONS = { linkedin: Linkedin, facebook: Facebook, instagram: Instagram };

const COMPANY_LINKS = [
  { label: 'About', href: '/about' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Ecosystem', href: '/ecosystem' },
  { label: 'Insights', href: '/insights' },
  { label: 'Contact', href: '/contact' },
];

const SERVICE_LINKS = [
  { label: 'Amden Operations', href: '/services/operations' },
  { label: 'Amden People', href: '/services/people' },
  { label: 'Amden Growth', href: '/services/growth' },
  { label: 'Amden Launch', href: '/services/launch' },
];

// Award badges — transparent PNGs (backgrounds removed), shown on white
// pills so the navy-text logos stay readable on the navy footer.
const AWARDS = [
  {
    slug: 'corporatelivewire-innovation',
    name: 'CorporateLiveWire Innovation & Excellence Awards — 2026 Winner',
    src: '/assets/awards/award-corporatelivewire-innovation-2026.png',
    width: 395,
    height: 168,
  },
  {
    slug: 'chambers-eir',
    name: 'eir Business & Chambers Ireland Awards 2026',
    src: '/assets/awards/award-chambers-eir-2026.png',
    width: 524,
    height: 134,
  },
  {
    slug: 'meath-business-tourism',
    name: 'Meath Business & Tourism Awards 2025',
    src: '/assets/awards/award-meath-business-tourism-2025.png',
    width: 274,
    height: 196,
  },
  {
    slug: 'national-digital',
    name: 'National Digital Awards — Finalist 2026 (Digital Business Ireland)',
    src: '/assets/awards/award-national-digital-2026.png',
    width: 218,
    height: 215,
  },
  {
    slug: 'family-business-awards',
    name: 'Family Business Awards 2026 — Finalist (in association with Goodbody)',
    src: '/assets/awards/award-family-business-2026.png',
    width: 300,
    height: 216,
  },
  {
    slug: 'lbi-awards',
    name: 'LBI Awards 2026 — Shortlisted',
    src: '/assets/awards/award-lbi-2026.png',
    width: 447,
    height: 216,
  },
];

const LEGAL_LINKS = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Cookie Policy', href: '/cookie-policy' },
  { label: 'Terms of Service', href: '/terms-of-service' },
  { label: 'Data Protection', href: '/data-protection' },
  { label: 'Accessibility', href: '/accessibility' },
];

const colHeading = 'text-xs font-bold uppercase tracking-[0.18em] text-white';
const linkCls = 'text-sm text-slate-400 transition-colors hover:text-white';

export const Footer = () => {
  useLiveTick();
  const CONTACT = getContact();
  const SOCIALS = getSocials();
  const companyLinks = getFooterCompany() || COMPANY_LINKS;
  const serviceLinks = getFooterServices() || SERVICE_LINKS;

  return (
  <footer data-testid="site-footer" className="bg-navy-950 text-slate-300">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr_1fr_1.3fr] lg:gap-10">
        <div>
          <img
            src="/assets/amden-logo-white.png"
            alt="Amden Group"
            width="200"
            height="47"
            className="h-11 w-auto"
            data-testid="footer-logo"
          />
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-slate-400">
            Practical, hands-on business consultancy helping Irish and UK businesses improve
            operations, people, growth and business systems.
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map((s) => {
              const Icon = SOCIAL_ICONS[s.icon];
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Amden Group on ${s.label}`}
                  data-testid={`footer-social-${s.icon}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/15 text-slate-300 transition-colors hover:border-gold-500 hover:text-gold-500"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </a>
              );
            })}
          </div>
          <p className="mt-6 text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
            {CONTACT.locationLine}
          </p>
        </div>

        <nav aria-label="Company">
          <h3 className={colHeading}>Company</h3>
          <ul className="mt-5 space-y-3">
            {companyLinks.map((l) => (
              <li key={l.label}>
                <Link to={l.href} className={linkCls} data-testid={`footer-company-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Services">
          <h3 className={colHeading}>Services</h3>
          <ul className="mt-5 space-y-3">
            {serviceLinks.map((l) => (
              <li key={l.label}>
                <Link to={l.href} className={linkCls} data-testid={`footer-service-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h3 className={colHeading}>Contact</h3>
          <ul className="mt-5 space-y-4 text-sm">
            <li>
              <a href={CONTACT.phoneHref} data-testid="footer-phone" className="group flex items-start gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" />
                <span className="text-slate-300 transition-colors group-hover:text-white">{CONTACT.phone}</span>
              </a>
            </li>
            <li>
              <a href={CONTACT.emailHref} data-testid="footer-email" className="group flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" />
                <span className="text-slate-300 transition-colors group-hover:text-white">{CONTACT.email}</span>
              </a>
            </li>
            <li className="flex items-start gap-3" data-testid="footer-address">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" />
              <span className="text-slate-400">{CONTACT.address}</span>
            </li>
            <li>
              <Link
                to="/contact#book-appointment"
                data-testid="footer-book-consultation"
                className="group flex items-start gap-3"
              >
                <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" aria-hidden="true" />
                <span className="font-semibold text-gold-500 transition-colors group-hover:text-gold-200">
                  Book a consultation
                </span>
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-8 gap-y-4 px-4 py-7 sm:px-6 lg:px-8" data-testid="footer-awards">
        <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">
          Recognition &amp; Awards
        </span>
        <ul className="flex flex-wrap items-center gap-3">
          {AWARDS.map((award) => (
            <li key={award.name}>
              <span
                className="inline-flex items-center rounded-lg bg-white px-3 py-2 shadow-sm"
                title={award.name}
              >
                <img
                  src={award.src}
                  alt={award.name}
                  width={award.width}
                  height={award.height}
                  loading="lazy"
                  className="h-9 w-auto"
                  data-testid={`footer-award-${award.slug}`}
                />
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
        <p className="text-xs text-slate-500" data-testid="footer-copyright">
          © {new Date().getFullYear()} Amden Group. All rights reserved.
        </p>
        <nav aria-label="Legal">
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {LEGAL_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.href}
                  className="text-xs text-slate-500 transition-colors hover:text-white"
                  data-testid={`footer-legal-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  </footer>
  );
};
